import { Box, ButtonBase, Typography } from "@mui/material";
import type { ReactNode } from "react";
import {
  MdOutlineCalendarToday,
  MdOutlineSchedule,
  MdOutlineWarningAmber,
} from "react-icons/md";

import type { HomeFilterKey, HomeSummary } from "../types/home.types";
import {
  formatShortDay,
  HOME_FILTER_LABELS,
  shiftDayKey,
} from "../utils/homeFilters";
import { HOME_CARD_SHADOW, HOME_COLORS } from "./homeTheme";

type AttentionKey = keyof HomeSummary["cards"];

interface AttentionCardsProps {
  today?: string;
  cards?: HomeSummary["cards"];
  onOpen(key: HomeFilterKey): void;
}

export function AttentionCards({ today, cards, onOpen }: AttentionCardsProps) {
  const defs: Array<{ key: AttentionKey; support: string; icon: ReactNode }> = [
    {
      key: "hoje",
      support: today ? `Início em ${formatShortDay(today)}` : "",
      icon: <MdOutlineCalendarToday size={18} />,
    },
    {
      key: "amanha",
      support: today
        ? `Início em ${formatShortDay(shiftDayKey(today, 1))}`
        : "",
      icon: <MdOutlineCalendarToday size={18} />,
    },
    {
      key: "aprovacaoParada",
      support: "Aguardando retorno do cliente",
      icon: <MdOutlineSchedule size={18} />,
    },
    {
      key: "semAprovacao",
      support: "Início em até 3 dias",
      icon: <MdOutlineWarningAmber size={18} />,
    },
  ];

  return (
    <Box
      component="section"
      aria-label="Atenção imediata"
      display="grid"
      gap={2}
      gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
    >
      {defs.map(({ key, support, icon }) => {
        const value = cards?.[key];
        const warn = key === "semAprovacao" && !!value;
        return (
          <ButtonBase
            key={key}
            onClick={() => onOpen(key)}
            data-alert={warn || undefined}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              textAlign: "left",
              gap: 0.5,
              p: 2,
              borderRadius: "8px",
              bgcolor: warn ? HOME_COLORS.warnBg : "#FFF",
              border: `1px solid ${
                warn ? HOME_COLORS.warnBorder : HOME_COLORS.line
              }`,
              boxShadow: HOME_CARD_SHADOW,
              color: warn ? HOME_COLORS.warnText : HOME_COLORS.ink,
              "&:hover": {
                borderColor: warn ? HOME_COLORS.warnText : HOME_COLORS.primary,
              },
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                sx={{
                  color: warn ? HOME_COLORS.warnText : HOME_COLORS.primary,
                }}
              >
                {icon}
              </Box>
              <Typography sx={{ fontSize: 12, color: HOME_COLORS.muted }}>
                Ver lista ›
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2 }}>
              {value ?? "–"}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {HOME_FILTER_LABELS[key]}
            </Typography>
            <Typography sx={{ fontSize: 12, color: HOME_COLORS.muted }}>
              {support}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
